import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, Button } from '@/components/common';
import { formatQuantity } from '@/utils/number-format';
import { useUIStore } from '@/store/uiStore';
import { materialApi } from '@/api/endpoints/materials';
import { Material, MaterialTransaction, MaterialStock } from '@/types/material';
import MaterialStockCardTab from '../MaterialStockCardTab';
import MaterialStockTab from '../MaterialStockTab';
import { formatDate, formatCurrency } from '@/utils/safe-formatting';

const MaterialDetailModal: React.FC = () => {
  const { t } = useTranslation();
  const { modals, closeModal, selectedMaterial } = useUIStore();
  const [loading, setLoading] = useState(false);
  const [material, setMaterial] = useState<Material | null>(null);
  const [transactions, setTransactions] = useState<MaterialTransaction[]>([]);
  const [stockRecords, setStockRecords] = useState<MaterialStock[]>([]);
  const [activeTab, setActiveTab] = useState<'info' | 'stockCard' | 'stock'>('info');

  const isOpen = modals.materialDetail;

  // Fetch material detail when modal opens
  useEffect(() => {
    if (isOpen && selectedMaterial) {
      fetchMaterialDetail();
    }
  }, [isOpen, selectedMaterial]);

  const fetchMaterialDetail = async () => {
    if (!selectedMaterial) return;

    setLoading(true);
    try {
      const response = await materialApi.getMaterialDetail(selectedMaterial);
      setMaterial(response.material);
      setTransactions(response.transactions);
      setStockRecords(response.stockRecords);
    } catch (error: any) {
      console.error('Error fetching material detail:', error);

      // Show user-friendly error message
      const errorMessage = error.message || 'Không thể tải thông tin nguyên liệu. Vui lòng thử lại.';
      alert(errorMessage);

      // Don't close modal on error, let user try again or close manually
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeModal('materialDetail');
    setMaterial(null);
    setTransactions([]);
    setStockRecords([]);
    setActiveTab('info');
  };

  // Remove local formatCurrency and formatDate functions since we're using the safe ones from utils

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Đang hoạt động' },
      inactive: { color: 'bg-red-100 text-red-800', label: 'Ngừng hoạt động' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getStockStatusBadge = (status: string) => {
    const statusConfig = {
      in_stock: { color: 'bg-green-100 text-green-800', label: t('materials.stockStatusLabels.in_stock') },
      low_stock: { color: 'bg-yellow-100 text-yellow-800', label: t('materials.stockStatusLabels.low_stock') },
      out_of_stock: { color: 'bg-red-100 text-red-800', label: t('materials.stockStatusLabels.out_of_stock') },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.in_stock;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const tabs = [
    { key: 'info', label: 'Thông tin', beta: false },
    { key: 'stockCard', label: 'Thẻ kho', beta: true },
    { key: 'stock', label: 'Tồn kho', beta: true },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('tooltips.materials.materialInfo')} // Title positioned top-left of modal as specified
      size="xl" // Modal wrapper centered on screen as specified
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : material ? (
        <div className="space-y-6">
          {/* Header - Material Name + Code shown top-left inside modal body as specified */}
          <div className="border-b border-gray-200 pb-4">
            <h2 className="text-xl font-semibold text-gray-900">{material.name}</h2>
            <p className="text-sm text-gray-600">Mã: {material.code}</p>
          </div>

          {/* Tabs - positioned directly under the title, centered as specified */}
          <div className="mobile-tab-scroll">
            <nav className="mobile-tab-list justify-start sm:justify-center" aria-label="Material Detail Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`mobile-tab-button border-b-2 ${
                    activeTab === tab.key
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="flex items-center space-x-1">
                    <span>{tab.label}</span>
                    {tab.beta && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Beta
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="min-h-96">
            {activeTab === 'info' && (
              /* Info Grid - responsive layout filling the modal as specified */
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Mã nguyên liệu</label>
                    <p className="mt-1 text-sm text-gray-900 font-mono">{material.code}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Danh mục</label>
                    <p className="mt-1 text-sm text-gray-900">{material.categoryName || material.categoryId}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Loại nguyên liệu</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {material.type === 'raw' ? 'Nguyên liệu thô' :
                       material.type === 'semi-product' ? 'Bán thành phẩm' :
                       material.type === 'semi-finished' ? 'Bán hoàn thiện' : material.type}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Đơn vị</label>
                    <p className="mt-1 text-sm text-gray-900">{material.unit || '-'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Giá chuẩn</label>
                    <p className="mt-1 text-sm text-gray-900 font-medium">{formatCurrency(material.standardPrice)}</p>
                  </div>

                  {material.cutting_type && material.cutting_type > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Loại cắt</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {material.cutting_type === 1 ? 'Cắt góc' :
                         material.cutting_type === 2 ? 'Cắt vuông' :
                         `Loại ${material.cutting_type}`}
                      </p>
                    </div>
                  )}

                  {material.cutting_size_id && material.cutting_size_id.trim() && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Kích thước cắt</label>
                      <p className="mt-1 text-sm text-gray-900">{material.cutting_size_id}</p>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nhóm hàng</label>
                    <p className="mt-1 text-sm text-gray-900">{material.categoryGroupName || material.categoryGroupId || '-'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nhà cung cấp</label>
                    <div className="mt-1">
                      <p className="text-sm text-gray-900 font-medium">{material.supplierNames?.[0] || material.supplierIds?.[0] || '-'}</p>
                      {material.supplierAddresses && material.supplierAddresses.length > 0 && (
                        <p className="text-xs text-gray-600">{material.supplierAddresses[0]}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Số lượng thực tế</label>
                      <p className="mt-1 text-sm text-gray-900 font-semibold">{formatQuantity(material.actualQuantity)} {material.unit}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('materials.fields.availableQuantity')}</label>
                      <p className="mt-1 text-sm text-green-600 font-semibold">{formatQuantity(material.availableQuantity)} {material.unit}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Đang giữ</label>
                      <p className="mt-1 text-sm text-orange-600 font-semibold">{formatQuantity(material.holdQuantity)} {material.unit}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Thiếu hụt</label>
                      <p className="mt-1 text-sm text-red-600 font-semibold">{formatQuantity(material.shortageQuantity)} {material.unit}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tồn kho tối thiểu</label>
                    <p className="mt-1 text-sm text-gray-900">{formatQuantity(material.minimumStock)} {material.unit}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Trạng thái</label>
                    <div className="mt-1">{getStatusBadge(material.status)}</div>
                  </div>
                </div>

                {/* Full width fields */}
                <div className="lg:col-span-2 space-y-4 pt-6 border-t border-gray-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Trạng thái kho</label>
                      <div className="mt-1">{getStockStatusBadge(material.stockStatus)}</div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Người tạo</label>
                      <p className="mt-1 text-sm text-gray-900">{material.createdBy || '-'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ngày tạo</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(material.createdAt)}</p>
                    </div>
                  </div>

                  {material.notes && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Ghi chú</label>
                      <div className="mt-1 p-3 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{material.notes}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'stockCard' && (
              <MaterialStockCardTab
                materialCode={material.code}
                transactions={transactions}
              />
            )}

            {activeTab === 'stock' && (
              <MaterialStockTab
                materialCode={material.code}
                stockRecords={stockRecords}
              />
            )}
          </div>

          {/* Save Button - bottom-right inside modal as specified */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button
              variant="primary"
              onClick={handleClose}
            >
              Đóng
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">Không tìm thấy thông tin nguyên liệu</p>
        </div>
      )}
    </Modal>
  );
};

export default MaterialDetailModal;
